import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import React, { useState } from 'react';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { useTheme } from 'styled-components';
import {
  DATA_GRID_COLUMN_TYPES,
  NUMERIC_QUESTION_MAX,
  QuestionOption,
} from 'src/types/formQuestionnaire';
import { PreviewAction } from '../FormPreview.reducer';
import {
  DataGridAddRowButton,
  DataGridColumn,
  DataGridColumnHeader,
  DataGridColumnHeaderText,
  DataGridContainer,
  DataGridRequiredTag,
  DataGridRow,
} from '../FormPreview.styles';
import { DatePickerField } from './DatePickerField';
import { renderInputField } from './shared';
import { BaseInputProps } from './types';

const DATA_GRID_NUMBER_MIN = 0;
const DATA_GRID_NUMBER_MAX = NUMERIC_QUESTION_MAX;
const DATA_GRID_NUMBER_STEP = 0.01;

export const getDataGridNumberError = (value: string): string | undefined => {
  const trimmed = value.trim();
  if (!trimmed) return undefined;
  if (/e/i.test(trimmed)) return 'Enter a valid number';
  const num = Number(trimmed);
  if (Number.isNaN(num)) return 'Enter a valid number';
  if (num < DATA_GRID_NUMBER_MIN) return 'Value must be 0 or greater';
  if (num > DATA_GRID_NUMBER_MAX) {
    return `Value must not exceed ${DATA_GRID_NUMBER_MAX.toLocaleString()}`;
  }
  const decimalPlaces = trimmed.split('.')[1]?.length ?? 0;
  if (decimalPlaces > 2) return 'Value must have at most 2 decimal places';
  return undefined;
};

const renderDataGridField = (
  col: QuestionOption,
  rowIndex: number,
  questionId: string,
  gridRows: Array<Record<string, string>>,
  dispatch: React.Dispatch<PreviewAction>,
  openPickerKey: string | null,
  setOpenPickerKey: (key: string | null) => void,
  textInputPlaceholder: string,
) => {
  const columnType = col.nextAction || DATA_GRID_COLUMN_TYPES.TEXT;
  const cellValue = gridRows[rowIndex]?.[col.id] || '';
  const placeholder = textInputPlaceholder;
  const cellKey = `${rowIndex}-${col.id}`;

  const dispatchGridCell = (value: string) =>
    dispatch({
      type: 'UPDATE_GRID_CELL',
      questionId,
      rowIndex,
      columnId: col.id,
      value,
    });

  switch (columnType) {
    case DATA_GRID_COLUMN_TYPES.NUMBER: {
      const numberError = getDataGridNumberError(cellValue);
      return renderInputField({
        type: DATA_GRID_COLUMN_TYPES.NUMBER,
        value: cellValue,
        onChange: (e) => dispatchGridCell(e.target.value),
        placeholder,
        testId: `data-grid-input-${rowIndex}-${col.id}`,
        min: DATA_GRID_NUMBER_MIN,
        max: DATA_GRID_NUMBER_MAX,
        step: DATA_GRID_NUMBER_STEP,
        error: !!numberError,
        helperText: numberError,
      });
    }
    case DATA_GRID_COLUMN_TYPES.DATE:
      return (
        <DatePickerField
          value={cellValue}
          onChange={(val) => dispatchGridCell(val)}
          usePortal
          isOpen={openPickerKey === cellKey}
          onToggle={(open) => setOpenPickerKey(open ? cellKey : null)}
          data-testid={`data-grid-input-${rowIndex}-${col.id}`}
        />
      );
    default:
      return renderInputField({
        value: cellValue,
        onChange: (e) => dispatchGridCell(e.target.value),
        placeholder,
        testId: `data-grid-input-${rowIndex}-${col.id}`,
      });
  }
};

export const DataGridInput: React.FC<BaseInputProps> = ({
  currentQuestion,
  answers,
  dispatch,
  inlinePlacement,
  textInputPlaceholder,
}) => {
  const theme = useTheme();
  const MAX_ROWS = 20;
  const columns = currentQuestion.options || [];
  const dataGridRowsForRender = answers[currentQuestion.id]?.dataGridRows || [
    {},
  ];
  const isMaxRowsReached = dataGridRowsForRender.length >= MAX_ROWS;
  const [openPickerKey, setOpenPickerKey] = useState<string | null>(null);

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <DataGridContainer
        $inlinePlacement={inlinePlacement}
        data-testid="form-preview-data-grid"
        onScroll={() => setOpenPickerKey(null)}
      >
        {dataGridRowsForRender.map((_, rowIndex) => (
          <DataGridRow key={rowIndex}>
            {columns.map((column, colIndex) => (
              <DataGridColumn
                key={column.id}
                $columnCount={columns.length}
                $inlinePlacement={inlinePlacement}
              >
                {rowIndex === 0 && (
                  <DataGridColumnHeader>
                    <DataGridColumnHeaderText>
                      {column.text}
                    </DataGridColumnHeaderText>
                    {colIndex === 0 && currentQuestion.isRequired && (
                      <DataGridRequiredTag>(Required)</DataGridRequiredTag>
                    )}
                  </DataGridColumnHeader>
                )}
                {renderDataGridField(
                  column,
                  rowIndex,
                  currentQuestion.id,
                  dataGridRowsForRender,
                  dispatch,
                  openPickerKey,
                  setOpenPickerKey,
                  textInputPlaceholder,
                )}
              </DataGridColumn>
            ))}
          </DataGridRow>
        ))}
      </DataGridContainer>
      <DataGridAddRowButton
        disabled={isMaxRowsReached}
        onClick={() =>
          dispatch({
            type: 'ADD_GRID_ROW',
            questionId: currentQuestion.id,
          })
        }
        data-testid="data-grid-add-row"
      >
        <StyledIcon
          className="uil uil-plus"
          size="24px"
          color={
            theme.isVaultVariant
              ? theme.newPalette.neutral.N800
              : theme.atlas.semantic.color.type.default
          }
        />
        Add new row
      </DataGridAddRowButton>
    </LocalizationProvider>
  );
};
